/**
 * Content Transitions Utility
 *
 * Provides smooth, "breathing" transitions for dynamic content updates.
 * Makes the app feel alive and magical with elegant animations.
 *
 * Usage:
 *   import { replaceContentSmoothly, fadeOut, fadeIn } from '../utils/content_transitions'
 *
 *   // Replace content with smooth transition
 *   await replaceContentSmoothly('#my-element', newHtml)
 *
 *   // Or use individual transitions
 *   await fadeOut(element)
 *   element.innerHTML = newContent
 *   await fadeIn(element)
 */

// Default timing configuration
const TIMING = {
  fadeOut: 250,    // Time to fade out (ms)
  fadeIn: 350,     // Time to fade in (ms)
  stagger: 30,     // Delay between staggered items (ms)
}

// Easing functions for natural motion
const EASING = {
  out: 'cubic-bezier(0.4, 0, 0.2, 1)',      // Smooth deceleration
  in: 'cubic-bezier(0.4, 0, 0.2, 1)',       // Smooth acceleration
  inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',    // Smooth both ways
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)', // Bouncy spring effect
}

/**
 * Fade out an element smoothly
 * @param {HTMLElement} element - The element to fade out
 * @param {Object} options - Configuration options
 * @returns {Promise} - Resolves when animation completes
 */
export function fadeOut(element, options = {}) {
  const duration = options.duration || TIMING.fadeOut
  const scale = options.scale !== false // Default to true

  return new Promise(resolve => {
    // Set up the transition
    element.style.transition = `opacity ${duration}ms ${EASING.out}, transform ${duration}ms ${EASING.out}`
    element.style.opacity = '0'

    if (scale) {
      element.style.transform = 'scale(0.98)'
    }

    setTimeout(resolve, duration)
  })
}

/**
 * Fade in an element smoothly with optional scale effect
 * @param {HTMLElement} element - The element to fade in
 * @param {Object} options - Configuration options
 * @returns {Promise} - Resolves when animation completes
 */
export function fadeIn(element, options = {}) {
  const duration = options.duration || TIMING.fadeIn
  const scale = options.scale !== false // Default to true
  const spring = options.spring || false

  return new Promise(resolve => {
    // Start from scaled down and transparent
    element.style.opacity = '0'
    if (scale) {
      element.style.transform = 'scale(0.98)'
    }

    // Force reflow to ensure the starting state is applied
    element.offsetHeight

    // Animate to final state
    const easing = spring ? EASING.spring : EASING.in
    element.style.transition = `opacity ${duration}ms ${easing}, transform ${duration}ms ${easing}`
    element.style.opacity = '1'
    element.style.transform = 'scale(1)'

    setTimeout(() => {
      // Clean up inline styles after animation
      element.style.transition = ''
      element.style.opacity = ''
      element.style.transform = ''
      resolve()
    }, duration)
  })
}

/**
 * Replace content smoothly with fade out/in transition
 * @param {string|HTMLElement} target - Selector or element to replace content in
 * @param {string} newHtml - New HTML content (replaces outerHTML if replaceOuter is true)
 * @param {Object} options - Configuration options
 * @returns {Promise<HTMLElement>} - The new element after replacement
 */
export async function replaceContentSmoothly(target, newHtml, options = {}) {
  const element = typeof target === 'string' ? document.querySelector(target) : target
  if (!element) {
    console.warn('replaceContentSmoothly: target element not found', target)
    return null
  }

  const replaceOuter = options.replaceOuter !== false // Default to true (replace outerHTML)
  const reinitAlpine = options.reinitAlpine !== false // Default to true

  // Fade out
  await fadeOut(element, options)

  // Replace content
  let newElement
  if (replaceOuter) {
    // Get the ID or create a temporary marker
    const id = element.id
    element.outerHTML = newHtml
    newElement = id ? document.getElementById(id) : document.querySelector(options.newSelector || target)
  } else {
    element.innerHTML = newHtml
    newElement = element
  }

  if (!newElement) {
    console.warn('replaceContentSmoothly: new element not found after replacement')
    return null
  }

  // Re-initialize Alpine.js components if present
  if (reinitAlpine && typeof Alpine !== 'undefined') {
    Alpine.initTree(newElement)
  }

  // Fade in with slight spring effect
  await fadeIn(newElement, { ...options, spring: options.spring !== false })

  return newElement
}

/**
 * Fetch HTML and replace content smoothly
 * @param {string} url - URL to fetch HTML from
 * @param {string|HTMLElement} target - Selector or element to replace
 * @param {Object} options - Configuration options
 * @returns {Promise<HTMLElement>} - The new element after replacement
 */
export async function fetchAndReplaceSmoothly(url, target, options = {}) {
  const element = typeof target === 'string' ? document.querySelector(target) : target
  if (!element) {
    console.warn('fetchAndReplaceSmoothly: target element not found', target)
    return null
  }

  // Start fade out immediately while fetching
  const fadeOutPromise = fadeOut(element, options)

  // Fetch new content
  const fetchPromise = fetch(url, {
    headers: {
      'Accept': 'text/html',
      'X-Requested-With': 'XMLHttpRequest',
      ...options.headers
    }
  })

  // Wait for both to complete
  const [, response] = await Promise.all([fadeOutPromise, fetchPromise])

  if (!response.ok) {
    // Fade back in on error
    await fadeIn(element, options)
    throw new Error(`Failed to fetch: ${response.status}`)
  }

  const html = await response.text()

  // Replace content
  const replaceOuter = options.replaceOuter !== false
  let newElement

  if (replaceOuter) {
    const id = element.id
    element.outerHTML = html
    newElement = id ? document.getElementById(id) : document.querySelector(options.newSelector || target)
  } else {
    element.innerHTML = html
    newElement = element
  }

  if (!newElement) {
    console.warn('fetchAndReplaceSmoothly: new element not found after replacement')
    return null
  }

  // Re-initialize Alpine.js components if present
  if (options.reinitAlpine !== false && typeof Alpine !== 'undefined') {
    Alpine.initTree(newElement)
  }

  // Fade in
  await fadeIn(newElement, { ...options, spring: options.spring !== false })

  return newElement
}

/**
 * Add a subtle highlight pulse to draw attention to updated content
 * @param {HTMLElement} element - The element to highlight
 * @param {Object} options - Configuration options
 */
export function highlightUpdate(element, options = {}) {
  const color = options.color || 'rgba(59, 130, 246, 0.1)' // Subtle blue
  const duration = options.duration || 600

  element.style.transition = `background-color ${duration}ms ${EASING.out}`
  element.style.backgroundColor = color

  setTimeout(() => {
    element.style.backgroundColor = ''
    setTimeout(() => {
      element.style.transition = ''
    }, duration)
  }, duration)
}

// Export timing config for customization
export { TIMING, EASING }
